import React from 'react';
import { Result, Button, Alert, Card, Space } from 'antd';
import { 
  ReloadOutlined, 
  HomeOutlined, 
  DisconnectOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  FileExclamationOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { ErrorInfo } from '../../utils/errorHandler';

/**
 * Error display types
 */
export type ErrorDisplayType = 'page' | 'inline' | 'card' | 'alert';

/**
 * Error display props
 */
export interface ErrorDisplayProps {
  error: ErrorInfo;
  type?: ErrorDisplayType;
  showRetry?: boolean;
  showHome?: boolean;
  onRetry?: () => void;
  onHome?: () => void;
  className?: string;
  style?: React.CSSProperties;
  extra?: React.ReactNode;
}

/**
 * Get appropriate icon based on error type
 */
const getErrorIcon = (errorType: ErrorInfo['type']) => {
  switch (errorType) {
    case 'network':
      return <DisconnectOutlined />;
    case 'permission':
      return <LockOutlined />;
    case 'business':
      return <ExclamationCircleOutlined />;
    case 'system':
      return <WarningOutlined />;
    case 'validation':
      return <FileExclamationOutlined />;
    default:
      return <ExclamationCircleOutlined />;
  }
};

/**
 * Get appropriate Result status based on error type
 */
const getResultStatus = (errorType: ErrorInfo['type']): 'error' | '403' | '404' | '500' => {
  switch (errorType) {
    case 'permission':
      return '403';
    case 'business':
      return 'error';
    case 'system':
      return '500';
    case 'network':
      return 'error';
    case 'validation':
      return 'error';
    default:
      return 'error';
  }
};

/**
 * Get appropriate Alert type based on error type
 */
const getAlertType = (errorType: ErrorInfo['type']): 'error' | 'warning' | 'info' => {
  switch (errorType) {
    case 'network':
      return 'error';
    case 'permission':
      return 'warning';
    case 'business':
      return 'error';
    case 'system':
      return 'error';
    case 'validation':
      return 'warning';
    default:
      return 'error';
  }
};

/**
 * Get user-friendly error title
 */
const getErrorTitle = (error: ErrorInfo): string => {
  switch (error.type) {
    case 'network':
      return '网络连接异常';
    case 'permission':
      return '权限不足';
    case 'business':
      return '操作失败';
    case 'system':
      return '系统错误';
    case 'validation':
      return '数据验证失败';
    default:
      return '发生错误';
  }
};

/**
 * Get detailed error description
 */
const getErrorDescription = (error: ErrorInfo): string => {
  let description = error.message;
  
  // Add helpful suggestions based on error type
  switch (error.type) {
    case 'network':
      description += '\n请检查网络连接后重试';
      break;
    case 'permission':
      description += '\n请联系管理员获取相应权限';
      break;
    case 'system':
      description += '\n如果问题持续存在，请联系技术支持';
      break;
    case 'validation':
      description += '\n请检查输入数据的格式和内容';
      break;
  }
  
  // Add trace ID if available
  if (error.traceId) {
    description += `\n追踪ID: ${error.traceId}`;
  }
  
  return description;
};

/**
 * Page-level error display using Result component
 */
const PageErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  showRetry = true,
  showHome = false,
  onRetry,
  onHome,
  className = '',
  extra,
}) => {
  const title = getErrorTitle(error);
  const description = getErrorDescription(error);
  const status = getResultStatus(error.type);
  
  const actions = [
    showRetry && onRetry && (
      <Button key="retry" type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
        重试
      </Button>
    ),
    showHome && onHome && (
      <Button key="home" icon={<HomeOutlined />} onClick={onHome}>
        返回首页
      </Button>
    ),
    extra,
  ].filter(Boolean);

  return (
    <div className={`error-display page-error ${className}`}>
      <Result
        status={status}
        title={title}
        subTitle={description}
        extra={actions.length > 0 ? actions : undefined}
      />
    </div>
  );
};

/**
 * Inline error display using Alert component
 */
const InlineErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  showRetry = false,
  onRetry,
  className = '',
  style,
}) => {
  const title = getErrorTitle(error);
  const description = error.message;
  const alertType = getAlertType(error.type);
  const icon = getErrorIcon(error.type);

  const action = showRetry && onRetry ? (
    <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
      重试
    </Button>
  ) : undefined;

  return (
    <div className={`error-display inline-error ${className}`} style={style}>
      <Alert
        message={title}
        description={description}
        type={alertType}
        icon={icon}
        showIcon
        action={action}
        closable={false}
      />
      {error.traceId && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
          追踪ID: {error.traceId}
        </div>
      )}
    </div>
  );
};

/**
 * Card-based error display
 */
const CardErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  showRetry = true,
  onRetry,
  className = '',
  style,
}) => {
  const title = getErrorTitle(error);
  const description = getErrorDescription(error);
  const icon = getErrorIcon(error.type);

  return (
    <div className={`error-display card-error ${className}`} style={style}>
      <Card>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }}>
            {icon}
          </div>
          <h3 style={{ marginBottom: 8 }}>{title}</h3>
          <p style={{ color: '#666', marginBottom: 16, whiteSpace: 'pre-line' }}>
            {description}
          </p>
          {showRetry && onRetry && (
            <Button type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
              重试
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

/**
 * Alert-based error display
 */
const AlertErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  showRetry = false,
  onRetry,
  className = '',
  style,
}) => {
  const title = getErrorTitle(error);
  const alertType = getAlertType(error.type);

  const action = showRetry && onRetry ? (
    <Space>
      <Button size="small" type="text" icon={<ReloadOutlined />} onClick={onRetry}>
        重试
      </Button>
    </Space>
  ) : undefined;

  return (
    <div className={`error-display alert-error ${className}`} style={style}>
      <Alert
        message={title}
        description={error.message}
        type={alertType}
        showIcon
        action={action}
        banner
      />
    </div>
  );
};

/**
 * Main error display component
 */
const ErrorDisplay: React.FC<ErrorDisplayProps> = (props) => {
  const { type = 'page' } = props;

  switch (type) {
    case 'inline':
      return <InlineErrorDisplay {...props} />;
    case 'card':
      return <CardErrorDisplay {...props} />;
    case 'alert':
      return <AlertErrorDisplay {...props} />;
    case 'page':
    default:
      return <PageErrorDisplay {...props} />;
  }
};

/**
 * Container-specific error displays
 */
export const ContainerErrorDisplays = {
  /**
   * Error display for container list loading failure
   */
  ListError: ({ error, onRetry }: { error: ErrorInfo; onRetry?: () => void }) => (
    <ErrorDisplay
      error={error}
      type="card"
      showRetry={error.type === 'network' || error.type === 'system'}
      onRetry={onRetry}
    />
  ),

  /**
   * Error display for container detail loading failure
   */
  DetailError: ({ error, onRetry, onHome }: { 
    error: ErrorInfo; 
    onRetry?: () => void; 
    onHome?: () => void;
  }) => (
    <ErrorDisplay
      error={error}
      type="page"
      showRetry={error.type === 'network' || error.type === 'system'}
      showHome={true}
      onRetry={onRetry}
      onHome={onHome}
    />
  ),

  /**
   * Error display for form submission failure
   */
  FormError: ({ error, onRetry }: { error: ErrorInfo; onRetry?: () => void }) => (
    <ErrorDisplay
      error={error}
      type="alert"
      showRetry={error.type === 'network'}
      onRetry={onRetry}
    />
  ),

  /**
   * Error display for inline operations
   */
  InlineError: ({ error, onRetry }: { error: ErrorInfo; onRetry?: () => void }) => (
    <ErrorDisplay
      error={error}
      type="inline"
      showRetry={error.type === 'network'}
      onRetry={onRetry}
    />
  ),
};

/**
 * React hook for error display
 */
export function useErrorDisplay() {
  return {
    ErrorDisplay,
    ContainerErrorDisplays,
    getErrorTitle,
    getErrorDescription,
    getResultStatus,
    getAlertType,
  };
}

export default ErrorDisplay;